import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserSettingsData } from 'api/types';
import { AxiosInstance } from 'axios';
import config from 'config';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

async function updateSettings(axios: AxiosInstance, userData: UserSettingsData): Promise<UserSettingsData> {
  const { data } = await axios.put(`${config.apiBaseUrl}/op/v1/settings`, {
    ...userData,
  });
  return data;
}

export default function useUpdateSettingsMutation() {
  const axios = useAxios();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: (userData: UserSettingsData) => updateSettings(axios, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Settings'] });
      queryClient.invalidateQueries({ queryKey: ['User'] });

      toast({
        title: t('user:personal_settings.success_message.label'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
  });
  useEffect(() => {
    if (mutation.isError) {
      const errorTitle = (mutation.error as any)?.response?.data?.errors?.spoken_languages[0];
      manageErrorResponse(toast, mutation.error as any, errorTitle, false);
    }
  }, [mutation.isError, mutation.error, toast]);
  return mutation;
}
